import { useCallback, useEffect, useState } from 'react';
import { TERMS_VERSION } from '../constants/termsOfService';
import { acceptTermsForUser, getTermsRequirementForUser } from '../services/termsService';

const TERMS_CACHE_TTL_MS = 3 * 60 * 1000;

const readTermsCache = (organizationId, userId) => {
  try {
    const raw = sessionStorage.getItem(`terms_req:${organizationId}:${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > TERMS_CACHE_TTL_MS) return null;
    return parsed.value;
  } catch {
    return null;
  }
};

const writeTermsCache = (organizationId, userId, value) => {
  try {
    sessionStorage.setItem(
      `terms_req:${organizationId}:${userId}`,
      JSON.stringify({ ts: Date.now(), value }),
    );
  } catch {
    // ignore quota / private mode
  }
};

export const useTermsAcceptance = ({ userId, organizationId, displayName, email }) => {
  const cached = userId && organizationId ? readTermsCache(organizationId, userId) : null;
  const [isChecking, setIsChecking] = useState(Boolean(userId && organizationId && !cached));
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRequired, setIsRequired] = useState(Boolean(cached?.required));
  const [termsVersion, setTermsVersion] = useState(cached?.termsVersion || TERMS_VERSION);
  const [error, setError] = useState('');

  const refresh = useCallback(async ({ force = false } = {}) => {
    if (!userId || !organizationId) {
      setIsRequired(false);
      setTermsVersion(TERMS_VERSION);
      setIsChecking(false);
      return;
    }

    if (!force) {
      const hit = readTermsCache(organizationId, userId);
      if (hit) {
        setIsRequired(Boolean(hit.required));
        setTermsVersion(hit.termsVersion || TERMS_VERSION);
        setIsChecking(false);
        return;
      }
    }

    setIsChecking(true);
    setError('');
    try {
      const requirement = await getTermsRequirementForUser({ organizationId, userId });
      setIsRequired(Boolean(requirement.required));
      setTermsVersion(requirement.termsVersion || TERMS_VERSION);
      writeTermsCache(organizationId, userId, {
        required: Boolean(requirement.required),
        termsVersion: requirement.termsVersion || TERMS_VERSION,
      });
    } catch (err) {
      console.error('Failed to check terms requirement:', err);
      setError(err.message || 'Failed to load Terms status');
      setIsRequired(false);
      setTermsVersion(TERMS_VERSION);
    } finally {
      setIsChecking(false);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const acceptTerms = useCallback(async ({ signatureName } = {}) => {
    if (!userId || !organizationId) return;

    setIsAccepting(true);
    setError('');
    try {
      await acceptTermsForUser({
        organizationId,
        userId,
        termsVersion,
        acceptedByName: signatureName || displayName,
        acceptedByEmail: email,
      });
      setIsRequired(false);
      writeTermsCache(organizationId, userId, { required: false, termsVersion });
    } catch (err) {
      console.error('Failed to accept terms:', err);
      setError(err.message || 'Failed to accept Terms');
      throw err;
    } finally {
      setIsAccepting(false);
    }
  }, [organizationId, userId, termsVersion, displayName, email]);

  return {
    isChecking,
    isAccepting,
    isRequired,
    termsVersion,
    error,
    acceptTerms,
    refresh: () => refresh({ force: true }),
  };
};
